import React, { useEffect, useState } from "react";
import {
  Car,
  Users,
  MessageSquare,
  Calendar,
  ClipboardList,
  TrendingUp,
  FileText
} from "lucide-react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useDispatch } from "react-redux";
import Snackbar1 from "../../Common/snackbar/components/snackbar";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const StatCard = ({ title, value, icon: Icon, colorClass }) => (
  <div className={`stat-card ${colorClass}`}>
    <div className="stat-content">
      <div className="stat-info">
        <h3>{title}</h3>
        <p className="stat-value">{value}</p>
      </div>
      <div className="stat-icon-wrapper">
        <Icon size={24} />
      </div>
    </div>
    <div className="stat-footer">
      <TrendingUp size={14} />
      <span>Updated just now</span>
    </div>
  </div>
);

const Dashboard = ({
  login,
  client,
  onLogout,
  snackbar,
  viewOldCarLength,
  viewTestDriveLength,
  viewContactUsLengths,
  viewAllVehicleEnq,
  viewAllService,
  close_snack_bar
}) => {
  const dispatch = useDispatch();
  const [contactData, setContactData] = useState({
    labels: [],
    datasets: [{ label: "Contacts", data: [], backgroundColor: "#818cf8" }],
  });

  const [testDriveData, setTestDriveData] = useState({
    labels: [],
    datasets: [{ label: "Test Drive", data: [], backgroundColor: "#2dd4bf" }],
  });

  const fetchData = () => {
    viewOldCarLength(login.token, login.user_id);
    viewTestDriveLength(login.token, login.user_id);
    viewContactUsLengths(login.token, login.user_id);
    // Fetching full data to get total counts for other cards
    if (viewAllVehicleEnq) viewAllVehicleEnq(login.token, login.user_id, "", 1, 1);
    if (viewAllService) viewAllService(login.token, login.user_id, "", 1, 1);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (client.viewContactUsLength) {
      const { labels, counts } = client.viewContactUsLength;
      setContactData({
        labels,
        datasets: [{
          label: "Contacts",
          data: counts,
          backgroundColor: "#818cf8",
          borderRadius: 6,
          borderSkipped: false,
        }],
      });
    }
    if (client.viewTestdriveLength) {
      const { labels, counts } = client.viewTestdriveLength;
      setTestDriveData({
        labels,
        datasets: [{
          label: "Test Drive",
          data: counts,
          backgroundColor: "#2dd4bf",
          borderRadius: 6,
          borderSkipped: false,
        }],
      });
    }
  }, [client.viewContactUsLength, client?.viewTestdriveLength]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          drawBorder: false,
          color: "rgba(0, 0, 0, 0.05)",
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <div className="dashboard-wrapper">
      <header className="dashboard-header">
        <div>
          <h1>Dashboard Overview</h1>
          <p className="subtitle">Welcome back! Here's what's happening today.</p>
        </div>
      </header>

      <div className="stats-grid">
        <StatCard
          title="Vehicle Enquiries"
          value={client?.all_vehicleEnqlength || 0}
          icon={ClipboardList}
          colorClass="indigo"
        />
        <StatCard
          title="Test Drives"
          value={client?.viewTestdriveLength?.totalLength || 0}
          icon={Calendar}
          colorClass="teal"
        />
        <StatCard
          title="Service Requests"
          value={client?.all_servicelength || 0}
          icon={Car}
          colorClass="blue"
        />
        <StatCard
          title="Contact Requests"
          value={client?.viewContactUsLength?.totalLength || 0}
          icon={Users}
          colorClass="purple"
        />
        <StatCard
          title="Old Cars"
          value={client?.oldcarlength || 0}
          icon={Car}
          colorClass="orange"
        />
      </div>

      <div className="charts-container">
        <div className="chart-wrapper">
          <div className="chart-header">
            <h3>Test Drive Activity</h3>
            <p>Recent bookings overview</p>
          </div>
          <div className="chart-content">
            <Bar data={testDriveData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-wrapper">
          <div className="chart-header">
            <h3>Contact Activity</h3>
            <p>Recent enquiries overview</p>
          </div>
          <div className="chart-content">
            <Bar data={contactData} options={chartOptions} />
          </div>
        </div>
      </div>

      <style>{`
        .dashboard-wrapper {
          padding: 32px;
          background: #f8fafc;
          min-height: calc(100vh - 64px);
        }

        .dashboard-header {
          margin-bottom: 32px;
        }

        .dashboard-header h1 {
          font-family: 'Outfit', sans-serif;
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 4px 0;
        }

        .subtitle {
          color: #64748b;
          font-size: 16px;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          border: 1px solid #e2e8f0;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }

        .stat-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .stat-info h3 {
          font-size: 14px;
          font-weight: 600;
          color: #64748b;
          margin: 0 0 8px 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 800;
          color: #1e293b;
          margin: 0;
          font-family: 'Outfit', sans-serif;
        }

        .stat-icon-wrapper {
          padding: 12px;
          border-radius: 12px;
        }

        .stat-footer {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #10b981;
          font-weight: 500;
        }

        /* Card Themes */
        .indigo .stat-icon-wrapper { background: #e0e7ff; color: #4338ca; }
        .teal .stat-icon-wrapper { background: #ccfbf1; color: #0f766e; }
        .blue .stat-icon-wrapper { background: #dbeafe; color: #1d4ed8; }
        .purple .stat-icon-wrapper { background: #f3e8ff; color: #7e22ce; }
        .orange .stat-icon-wrapper { background: #ffedd5; color: #c2410c; }

        /* Charts Section */
        .charts-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
          gap: 24px;
        }

        .chart-wrapper {
          background: white;
          padding: 24px;
          border-radius: 16px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
        }

        .chart-header {
          margin-bottom: 24px;
        }

        .chart-header h3 {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 4px 0;
        }

        .chart-header p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        .chart-content {
          height: 300px;
        }

        @media (max-width: 640px) {
          .dashboard-wrapper { padding: 20px; }
          .stats-grid { grid-template-columns: 1fr; }
          .charts-container { grid-template-columns: 1fr; }
          .chart-content { height: 250px; }
        }
      `}</style>
      <Snackbar1
        open={snackbar.response_received}
        close_snack_bar={() => close_snack_bar()}
        message={snackbar.message}
      />
    </div>
  );
};

export default Dashboard;
